import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Alert,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { formatCurrency } from '../../utils/formatters';
import DateRangeFilter from '../../components/DateRangeFilter';
import dayjs from 'dayjs';

const initialDateFilter = {
  year: dayjs().year(),
  month: dayjs().month(),
  // dateRange: undefined, // Only use year/month for initial state
};

const JobList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    filteredJobs: 0,
    completedJobs: 0,
    unassignedJobs: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    plPosition: '',
    van: '',
    channel: '',
    paymentStatus: '',
  });
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [channels, setChannels] = useState([]);

  const handleDateFilterChange = (newValue) => {
    // Only update if the mode or value actually changes
    const modeChanged =
      (!!newValue.dateRange !== !!dateFilter.dateRange) ||
      (newValue.year !== dateFilter.year) ||
      (newValue.month !== dateFilter.month);

    let rangeChanged = false;
    if (newValue.dateRange && dateFilter.dateRange) {
      rangeChanged =
        newValue.dateRange[0]?.format('YYYY-MM-DD') !== dateFilter.dateRange[0]?.format('YYYY-MM-DD') ||
        newValue.dateRange[1]?.format('YYYY-MM-DD') !== dateFilter.dateRange[1]?.format('YYYY-MM-DD');
    }

    if (modeChanged || rangeChanged) {
      setDateFilter(newValue);
    }
  };

  const fetchData = useCallback(async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      // Build params based on dateFilter mode
      const params = {
        search: filters.search,
        status: filters.status.length > 0 ? filters.status : undefined,
        plPosition: filters.plPosition,
        channel: filters.channel,
        van: filters.van,
        paymentStatus: filters.paymentStatus || undefined,
        page,
        limit,
      };
      if (dateFilter.dateRange) {
        params.startDate = dateFilter.dateRange[0]?.format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1]?.format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      }
      const [jobsResponse, vansResponse] = await Promise.all([
        apiService.jobs.getAll(params),
        apiService.vans.getAll(),
      ]);

      // Expecting jobsResponse.data to be { data: [...], total: N, filteredTotal: N }
      const jobsData = jobsResponse.data.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setFilteredTotal(jobsResponse.data.filteredTotal || 0);
      setVans(vansResponse.data || []);

      // Use counts directly from the API response
      setStats({
        totalJobs: jobsResponse.data.total || 0,
        filteredJobs: jobsResponse.data.filteredTotal || 0,
        completedJobs: jobsResponse.data.completedTotal || 0,
        unassignedJobs: jobsResponse.data.unassignedTotal || 0,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.message || 'Failed to fetch data');
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [filters, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await apiService.channels.getAll();
        setChannels(res.data || []);
      } catch (err) {
        console.error('Failed to fetch channels', err);
      }
    };
    fetchChannels();
  }, []);

  const handleDelete = async (id) => {
    try {
      await apiService.jobs.delete(id);
      message.success('Job deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting job:', error);
      message.error('Failed to delete job');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'processing';
      case 'cancelled':
        return 'error';
      case 'unassigned':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'bookingReference',
      key: 'bookingReference',
      render: (text, record) => (
        <a
          href={`/jobs/${record._id}`}
          style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => { if (e.ctrlKey || e.metaKey || e.button === 1) return; e.preventDefault(); navigate(`/jobs/${record._id}`); }}
        >
          {text || 'No Reference'}
        </a>
      ),
    },
    {
      title: 'Driver',
      dataIndex: ['driver', 'name'],
      key: 'driver',
      render: (text, record) => text || record.driver?.name || 'Unassigned',
    },
    {
      title: 'Van',
      dataIndex: ['van', 'registration'],
      key: 'van',
      render: (text, record) => text || record.van?.registration || 'Unassigned',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'jobStatus',
      key: 'jobStatus',
      render: (jobStatus) => (
        <Tag color={getStatusColor(jobStatus)}>
          {jobStatus?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Net Value',
      dataIndex: ['payment', 'amount'],
      key: 'netValue',
      align: 'right',
      render: (amount, record) => {
        if (!amount && !record.payment?.amount) return 'N/A';
        return formatCurrency(amount || record.payment?.amount);
      },
    },
    {
      title: 'PL Position',
      dataIndex: 'plPosition',
      key: 'plPosition',
      render: (plPosition) => (
        <Tag color={plPosition === 'income' ? 'green' : 'volcano'}>
          {plPosition?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<CopyOutlined />}
            onClick={() => navigate(`/jobs/create?clone=${record._id}`)}
          >
            Clone
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/jobs/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this job?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Jobs (All)"
              value={stats.totalJobs}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Filtered Jobs"
              value={stats.filteredJobs}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed Jobs (Filtered)"
              value={stats.completedJobs}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Unassigned Jobs (Filtered)"
              value={stats.unassignedJobs}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        {/* First row: Date range only */}
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24, flexWrap: 'wrap' }}>
          <Col xs={24} sm={12} md={10} lg={8} xl={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Date Range:</span>
              <DateRangeFilter value={dateFilter} onChange={handleDateFilterChange} />
            </Space>
          </Col>
        </Row>
        {/* Second row: Search and other filters */}
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }}>
          <Col flex="220px" style={{ marginRight: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Search</span>
              <Input.Search
                placeholder="Search by reference, customer, etc."
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                onSearch={() => fetchData(1, pageSize)}
                allowClear
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
          <Col flex="180px">
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Status</span>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Status"
                value={filters.status}
                onChange={val => setFilters(f => ({ ...f, status: val }))}
              >
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="assigned">Assigned</Select.Option>
                <Select.Option value="unassigned">Unassigned</Select.Option>
                <Select.Option value="cancelled">Cancelled</Select.Option>
              </Select>
            </Space>
          </Col>
          <Col flex="180px">
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Payment Status</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Payment Status"
                value={filters.paymentStatus || undefined}
                onChange={val => setFilters(f => ({ ...f, paymentStatus: val }))}
              >
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="paid">Paid</Select.Option>
                <Select.Option value="overdue">Overdue</Select.Option>
                <Select.Option value="cancelled">Cancelled</Select.Option>
              </Select>
            </Space>
          </Col>
          <Col flex="180px">
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>PL Position</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="PL Position"
                value={filters.plPosition || undefined}
                onChange={val => setFilters(f => ({ ...f, plPosition: val }))}
              >
                <Select.Option value="income">Income</Select.Option>
                <Select.Option value="expense">Expense</Select.Option>
              </Select>
            </Space>
          </Col>
          <Col flex="180px">
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Channel</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Channel"
                value={filters.channel || undefined}
                onChange={val => setFilters(f => ({ ...f, channel: val }))}
              >
                {channels.map(channel => (
                  <Select.Option key={channel._id || channel.name} value={channel.name}>{channel.name}</Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col flex="180px">
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ fontWeight: 500 }}>Van</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Van"
                value={filters.van || undefined}
                onChange={val => setFilters(f => ({ ...f, van: val }))}
              >
                {vans.map(van => (
                  <Select.Option key={van._id || van.registration} value={van._id || van.registration}>{van.registration}</Select.Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/jobs/create')}
          >
            Create Job
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
          }}
        />
      </Card>
    </div>
  );
};

export default JobList; 